import React from 'react';
import { Box, Typography, Grid, Container, Paper, LinearProgress, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import aboutimage from "../images/about_vision.png";
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PsychologyIcon from '@mui/icons-material/Psychology';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SecurityIcon from '@mui/icons-material/Security';

// Data for Core Values
const coreValues = [
    { title: "Innovation", desc: "We continually challenge the status quo to pioneer the technologies of tomorrow.", icon: <RocketLaunchIcon fontSize="large" />, color: "#00E5FF" },
    { title: "Client Centricity", desc: "We are dedicated to the success of our clients, tailoring our solutions to their unique strategic goals.", icon: <HandshakeIcon fontSize="large" />, color: "#E040FB" },
    { title: "Integrity", desc: "We uphold the highest standards of transparency and ethics in every partnership we forge.", icon: <VerifiedUserIcon fontSize="large" />, color: "#00C853" },
    { title: "Agility", desc: "We navigate market complexities with speed and precision, ensuring you remain ahead of the curve.", icon: <PsychologyIcon fontSize="large" />, color: "#FF9800" },
    { title: "Collaboration", desc: "We believe in the power of shared intelligence, working seamlessly with your teams.", icon: <Diversity3Icon fontSize="large" />, color: "#2979FF" },
    { title: "Excellence", desc: "We are committed to delivering world-class quality in every line of code and every interaction.", icon: <EmojiEventsIcon fontSize="large" />, color: "#FF3D00" }
];

// Data for Tech Expertise
const expertise = [
    { area: "Artificial Intelligence & ML", level: 95, color: "#E040FB" },
    { area: "Cloud Infrastructure (AWS/Azure)", level: 90, color: "#2979FF" },
    { area: "Full-Stack Web Development", level: 98, color: "#00E5FF" },
    { area: "Blockchain & Web3", level: 85, color: "#00C853" },
    { area: "Cybersecurity & InfoSec", level: 88, color: "#FF3D00" },
];

const SectionHeader = ({ tagline, title, subtitle, center = false }) => (
    <Box mb={{ xs: 5, md: 6 }} textAlign={center ? "center" : "left"}>
        {tagline && (
            <Typography variant="overline" sx={{ color: '#1A9E7F', letterSpacing: '0.2em', fontWeight: 700 }}>
                {tagline}
            </Typography>
        )}
        <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, color: '#034522', fontFamily: '"Lato", sans-serif', fontSize: { xs: '2rem', md: '3rem' } }}>
            {title}
        </Typography>
        {subtitle && (
            <Typography variant="h6" sx={{ mt: 2, color: '#333', maxWidth: '700px', mx: center ? 'auto' : 0, lineHeight: 1.6, fontWeight: 400, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                {subtitle}
            </Typography>
        )}
    </Box>
);

function About() {
    const navigate = useNavigate();
    return (
        <Box sx={{ overflowX: 'hidden', bgcolor: '#fff' }}>

            {/* 🔹 HERO SECTION */}
            {/* 🔹 HERO SECTION - PREMIUM ANIMATED */}
            <Box sx={{ bgcolor: '#002613', color: 'white', pt: { xs: 15, md: 20 }, pb: { xs: 10, md: 20 }, position: 'relative', overflow: 'hidden' }}>
                {/* Animated Background Elements */}
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'linear-gradient(135deg, #001e0f 0%, #00331a 100%)', zIndex: 0 }} />
                <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 8, repeat: Infinity }} style={{ position: 'absolute', top: '-20%', right: '-10%', width: '800px', height: '800px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(29, 233, 182, 0.1) 0%, transparent 60%)', zIndex: 0 }} />
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 100, repeat: Infinity, ease: 'linear' }} style={{ position: 'absolute', bottom: '-40%', left: '-20%', width: '1200px', height: '1200px', border: '1px solid rgba(255,255,255,0.03)', borderRadius: '40%', zIndex: 0 }} />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <Typography variant="overline" sx={{ color: '#69F0AE', letterSpacing: 4, fontWeight: 700, mb: 2, display: 'block' }}>Who We Are</Typography>
                        <Typography variant="h1" sx={{
                            fontSize: { xs: '2.5rem', md: '5rem' },
                            fontWeight: 800,
                            lineHeight: 1.1,
                            mb: 4,
                            background: 'linear-gradient(to right, #fff, #b9f6ca)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontFamily: '"Lato", sans-serif'
                        }}>
                            Engineering the Future of Enterprise
                        </Typography>
                        <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: '800px', mx: 'auto', lineHeight: 1.6, fontWeight: 400, fontSize: { xs: '1rem', md: '1.5rem' }, color: '#f0f0f0' }}>
                            We are a global technology consulting firm dedicated to accelerating digital transformation for the world's most ambitious organizations.
                        </Typography>
                    </motion.div>
                </Container>
            </Box>

            {/* 🔹 AWARDS & RECOGNITION */}
            <Box sx={{ py: 10, bgcolor: '#FAFAFA', borderBottom: '1px solid #eee' }}>
                <Container maxWidth="lg">
                    <Typography variant="overline" color="text.secondary" fontWeight={700} display="block" textAlign="center" mb={4} letterSpacing={2}>Recognized by Industry Leaders</Typography>
                    <Grid container spacing={4} justifyContent="center" alignItems="center">
                        {[
                            { award: "Tech Innovator 2024", org: "Global Tech Summit" },
                            { award: "Best AI Solution", org: "Enterprise Excellence Awards" },
                            { award: "Top 10 Startups", org: "Venture Daily" },
                            { award: "Excellence in Code", org: "DevPro Magazine" }
                        ].map((item, i) => (
                            <Grid item xs={6} md={3} key={i} textAlign="center">
                                <Box sx={{ p: 2, opacity: 0.7, transition: '0.3s', '&:hover': { opacity: 1, transform: 'scale(1.05)' } }}>
                                    <Box sx={{ width: 60, height: 60, bgcolor: '#E8F5E9', borderRadius: '50%', mx: 'auto', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <EmojiEventsIcon sx={{ color: '#1A9E7F' }} />
                                    </Box>
                                    <Typography variant="h6" fontWeight={800} color="#034522" fontSize="1.1rem">{item.award}</Typography>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>{item.org}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 🔹 OUR STORY & IMAGE */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 15 } }}>
                <Grid container spacing={8} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
                            <Box sx={{ position: 'relative' }}>
                                <Box sx={{ position: 'absolute', top: -20, left: -20, width: '100%', height: '100%', border: '2px solid #69F0AE', borderRadius: 4, display: { xs: 'none', md: 'block' } }} />
                                <Box component="img" src={aboutimage} alt="Office" sx={{ width: '100%', borderRadius: 4, position: 'relative', zIndex: 1, boxShadow: '0 20px 50px rgba(0,0,0,0.1)' }} />
                            </Box>
                        </motion.div>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
                            <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>Our Journey</Typography>
                            <Typography variant="h3" fontWeight={800} sx={{ color: '#034522', mb: 3, mt: 1 }}>Defining the Next Era</Typography>
                            <Typography paragraph sx={{ color: '#333', lineHeight: 1.8, fontSize: '1.1rem', mb: 2, fontWeight: 500 }}>
                                Established with a vision to bridge the gap between theoretical AI and practical enterprise application, GrowAITech has evolved into a powerhouse of digital innovation. We partner with global enterprises to navigate the complexities of the digital age, transforming challenges into sustainable competitive advantages.
                            </Typography>
                            <Typography paragraph sx={{ color: '#333', lineHeight: 1.8, fontSize: '1.1rem', mb: 2, fontWeight: 500 }}>
                                Our growth has been driven by a singular philosophy: <strong>Technology must drive business value.</strong> From complex cloud migrations to deploying autonomous AI agents, our solutions are engineered to be robust, scalable, and future-proof.
                            </Typography>
                            <Typography paragraph sx={{ color: '#333', lineHeight: 1.8, fontSize: '1.1rem', fontWeight: 500 }}>
                                We do not just write software; we architect the systems that underpin the global economy. Our team of world-class engineers and strategists is dedicated to one mission: Empowering your organization to lead in a digital-first world.
                            </Typography>

                            <Box mt={3} mb={4}>
                                {["Bootstrapped to Profitability in Year 1", "Expanded to 3 Continents", "Launch of Proprietary AI Engine"].map((milestone, i) => (
                                    <Box key={i} display="flex" alignItems="center" gap={1.5} mb={1}>
                                        <VerifiedUserIcon sx={{ color: '#69F0AE', fontSize: 20 }} />
                                        <Typography variant="body2" fontWeight={600} color="#034522">{milestone}</Typography>
                                    </Box>
                                ))}
                            </Box>
                            <Grid container spacing={4} mt={4}>
                                {[
                                    { label: "Years Exp.", value: "2+" },
                                    { label: "Projects", value: "100+" },
                                    { label: "Experts", value: "20+" }
                                ].map((stat, i) => (
                                    <Grid item xs={4} key={i}>
                                        <Box>
                                            <Typography variant="h3" fontWeight={800} color="#1A9E7F" sx={{ fontSize: { xs: '2rem', md: '3rem' } }}>{stat.value}</Typography>
                                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}>{stat.label}</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>

            {/* 🔹 STRATEGIC ROADMAP - TIMELINE */}
            <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff', borderBottom: '1px solid #f0f0f0' }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={10}>
                        <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>Evolution</Typography>
                        <Typography variant="h3" fontWeight={800} sx={{ color: '#034522', mt: 1 }}>Our Strategic Roadmap</Typography>
                    </Box>
                    <Box sx={{ position: 'relative' }}>
                        {/* Vertical Line */}
                        <Box sx={{ position: 'absolute', left: { xs: 20, md: '50%' }, top: 0, bottom: 0, width: 4, bgcolor: '#E0E0E0', transform: { md: 'translateX(-50%)' } }} />

                        {[
                            { year: "2021", title: "Inception & Seed", desc: "Founded with a core team of 5 engineers. Secured pre-seed funding to build our proprietary AI MVP.", align: "left" },
                            { year: "2022", title: "Market Penetration", desc: "Launched services in FinTech and Healthcare. Achieved 300% YoY growth and incorporated in Delaware.", align: "right" },
                            { year: "2023", title: "Global Expansion", desc: "Opened delivery centers in Chennai and Dubai. Partnered with AWS and Microsoft for cloud solutions.", align: "left" },
                            { year: "2024", title: "Enterprise Scale", desc: "Reached 100+ employees. Released 'GAT-Core', our enterprise middleware platform.", align: "right" },
                            { year: "2025", title: "Future Horizon", desc: "Investing $2M in Green Tech R&D and Quantum Computing readiness.", align: "left" }
                        ].map((item, i) => (
                            <Grid container key={i} sx={{ mb: 6, position: 'relative', justifyContent: { xs: 'flex-start', md: item.align === 'left' ? 'flex-end' : 'flex-start' }, flexDirection: { xs: 'row', md: item.align === 'left' ? 'row-reverse' : 'row' } }}>

                                {/* Desktop: Empty space for alternating effect */}
                                <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }} />

                                {/* Content Card */}
                                <Grid item xs={12} md={6} sx={{ pl: { xs: 8, md: item.align === 'left' ? 0 : 6 }, pr: { xs: 0, md: item.align === 'left' ? 6 : 0 } }}>
                                    <Paper elevation={0} sx={{ p: 4, bgcolor: '#FAFAFA', borderRadius: 4, border: '1px solid #eee', position: 'relative', '&:hover': { borderColor: '#1A9E7F', transform: 'translateY(-5px)', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }, transition: '0.3s', textAlign: 'left' }}>
                                        <Typography variant="h4" fontWeight={900} color="#E0E0E0" sx={{ position: 'absolute', top: 10, right: 20, fontSize: '3rem', opacity: 0.5 }}>{item.year}</Typography>
                                        <Typography variant="h6" fontWeight={800} color="#034522" sx={{ position: 'relative', zIndex: 1 }}>{item.title}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, position: 'relative', zIndex: 1, lineHeight: 1.6 }}>{item.desc}</Typography>
                                    </Paper>
                                </Grid>

                                {/* Timeline Dot */}
                                <Box sx={{ position: 'absolute', left: { xs: 20, md: '50%' }, top: 30, width: 22, height: 22, bgcolor: '#1A9E7F', borderRadius: '50%', transform: 'translateX(-50%)', border: '4px solid white', boxShadow: '0 0 0 4px #E0E0E0', zIndex: 2 }} />
                            </Grid>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* 🔹 VALUES SECTION */}
            <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#F0F9F6' }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={8}>
                        <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>Our DNA</Typography>
                        <Typography variant="h3" fontWeight={800} sx={{ color: '#034522', mt: 1 }}>Core Values</Typography>
                    </Box>
                    <Grid container spacing={4}>
                        {coreValues.map((val, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.1, duration: 0.5 }}
                                >
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 4,
                                            borderRadius: 4,
                                            bgcolor: '#fff',
                                            height: '100%',
                                            transition: 'all 0.3s',
                                            border: '1px solid transparent',
                                            '&:hover': { transform: 'translateY(-10px)', boxShadow: `0 20px 40px ${val.color}30`, borderColor: val.color }
                                        }}
                                    >
                                        <Box sx={{ color: val.color, mb: 2, transform: 'scale(1.2)', display: 'inline-block', p: 1, borderRadius: '50%', bgcolor: `${val.color}10` }}>{val.icon}</Box>
                                        <Typography variant="h5" fontWeight={700} gutterBottom color="#034522">{val.title}</Typography>
                                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>{val.desc}</Typography>
                                    </Paper>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 🔹 CORPORATE GOVERNANCE & ETHICS */}
            <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#FAFAFA' }}>
                <Container maxWidth="lg">
                    <SectionHeader title="Governance & Compliance" subtitle="We operate with an unwavering commitment to legal compliance, data privacy, and ethical business standards." center />
                    <Grid container spacing={4} mt={3}>
                        {[
                            { title: "Data Privacy (GDPR/CCPA)", desc: "We adhere to the strictest global data protection regulations to ensure your user data remains inviolable.", icon: <VerifiedUserIcon /> },
                            { title: "Ethical AI Framework", desc: "Our AI solutions are audited for bias, fairness, and transparency before deployment.", icon: <PsychologyIcon /> },
                            { title: "ISO 27001 Certified", desc: "Our information security management systems meet international benchmarks for best practices.", icon: <SecurityIcon /> }
                        ].map((gov, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Box sx={{ p: 4, bgcolor: 'white', borderRadius: 4, border: '1px solid #eee', height: '100%', textAlign: 'center' }}>
                                    <Box sx={{ color: '#034522', mb: 2 }}>{gov.icon}</Box>
                                    <Typography variant="h6" fontWeight={700} gutterBottom>{gov.title}</Typography>
                                    <Typography variant="body2" color="text.secondary">{gov.desc}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 🔹 STRATEGIC INNOVATION ARCHITECTURE - NEW SECTION */}
            <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#FAFAFA' }}>
                <Container maxWidth="lg">
                    <SectionHeader
                        tagline="Future-Ready Paradigms"
                        title="Strategic Innovation Architecture"
                        subtitle="Pioneering the next frontier of digital capability through rigorous R&D and proprietary DeepTech frameworks."
                        center
                    />

                    <Grid container spacing={4} mt={6}>
                        {[
                            {
                                title: "Quantum-Resistant Cryptography",
                                tech: "Cybersecurity",
                                desc: "Architecting post-quantum security layers that future-proof your data against the decryption capabilities of next-gen quantum processors. We implement lattice-based cryptography today to secure your tomorrow."
                            },
                            {
                                title: "Neuromorphic AI Engineering",
                                tech: "Artificial Intelligence",
                                desc: "Moving beyond traditional neural networks to bio-inspired event-driven architectures. Our models mimic the energy efficiency and adaptability of the human brain for real-time edge decision making."
                            },
                            {
                                title: "Autonomous Edge Ecosystems",
                                tech: "IoT & 5G",
                                desc: "Decentralized intelligence grids where IoT devices collaborate purely peer-to-peer. We build self-healing meshes that maintain operational integrity even when fully disconnected from the cloud."
                            },
                            {
                                title: "Generative Design Webs",
                                tech: "Digital Twin",
                                desc: "Utilizing Generative Adversarial Networks (GANs) to autonomously simulate millions of supply chain scenarios, optimizing logistics and manufacturing workflows before physical implementation."
                            }
                        ].map((card, i) => (
                            <Grid item xs={12} md={6} key={i}>
                                <Paper elevation={0} sx={{
                                    p: 5,
                                    height: '100%',
                                    borderRadius: 6,
                                    bgcolor: '#fff',
                                    border: '1px solid #eee',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-10px) scale(1.02)',
                                        boxShadow: '0 30px 60px rgba(0,38,19,0.1)',
                                        borderColor: '#1A9E7F'
                                    }
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                        <Box sx={{ p: 1, bgcolor: '#F0F9F6', borderRadius: 2, color: '#1A9E7F', fontWeight: 800, fontSize: '0.75rem', letterSpacing: 1, textTransform: 'uppercase' }}>
                                            {card.tech}
                                        </Box>
                                        <Typography variant="h2" fontWeight={900} sx={{ color: '#E0E0E0', fontSize: '2.5rem' }}>0{i + 1}</Typography>
                                    </Box>
                                    <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color: '#034522', fontSize: '1.5rem', lineHeight: 1.3 }}>
                                        {card.title}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.8, fontSize: '1.05rem' }}>
                                        {card.desc}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 🔹 EXECUTIVE LEADERSHIP - NEW SECTION */}
            <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff' }}>
                <Container maxWidth="lg">
                    <SectionHeader
                        tagline="Visionary Minds"
                        title="Executive Leadership"
                        subtitle="Guided by a team of industry veterans committed to engineering excellence and ethical innovation."
                        center
                    />

                    <Grid container spacing={4} mt={6}>
                        {[
                            { name: "Kavin Rajendran", role: "Founder & MD", image: require('../images/kavin_rajendran.jpg') },
                            { name: "Koushik Mahendran", role: "Co-Founder & CEO", image: "https://ui-avatars.com/api/?name=Koushik+Mahendran&background=002613&color=fff" },
                            { name: "Harish Ragupathy", role: "Executive Director", image: require('../images/harish_ragupathy.png') },
                            { name: "Karan Raj Thangavel", role: "Marketing Analyst", image: require('../images/karan_raj_thangavel.png') }
                        ].map((leader, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Paper elevation={0} sx={{
                                    p: 4,
                                    height: '100%',
                                    borderRadius: 6,
                                    bgcolor: '#FAFAFA',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    transition: '0.3s',
                                    '&:hover': {
                                        transform: 'translateY(-10px)',
                                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                                        bgcolor: '#fff'
                                    }
                                }}>
                                    <Box
                                        component="img"
                                        src={leader.image}
                                        alt={leader.name}
                                        sx={{
                                            width: 120,
                                            height: 120,
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            mb: 3,
                                            border: '4px solid #fff',
                                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                        }}
                                    />
                                    <Typography variant="h6" fontWeight={800} color="#034522" gutterBottom>{leader.name}</Typography>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: '#1A9E7F', textTransform: 'uppercase', letterSpacing: 1 }}>{leader.role}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 🔹 ENGINEERING EXCELLENCE */}
            <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#002613', color: 'white', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'radial-gradient(circle at 0% 50%, rgba(105, 240, 174, 0.1), transparent 50%)' }} />
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={8} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Typography variant="overline" sx={{ color: '#69F0AE', letterSpacing: 2, fontWeight: 700 }}>Our Standard</Typography>
                            <Typography variant="h3" fontWeight={800} sx={{ mt: 1, mb: 3 }}>Engineering Excellence</Typography>
                            <Typography paragraph sx={{ lineHeight: 1.8, opacity: 0.8, fontSize: '1.1rem' }}>
                                We don't believe in "good enough." Every system we architect is built to withstand extreme scale, security threats, and future pivotability.
                            </Typography>
                            <Box mt={4}>
                                {[
                                    { title: "DevSecOps First", desc: "Security is baked in from line one, not added as an afterthought." },
                                    { title: "Clean Architecture", desc: "Modularity that allows your product to evolve without technical debt." },
                                    { title: "99.99% Uptime", desc: "Infrastructure designed for mission-critical reliability." }
                                ].map((item, i) => (
                                    <Box key={i} mb={3} display="flex" gap={2}>
                                        <Box sx={{ minWidth: 4, height: 50, bgcolor: '#69F0AE', borderRadius: 2 }} />
                                        <Box>
                                            <Typography variant="h6" fontWeight={700} color="#fff">{item.title}</Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.7 }}>{item.desc}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Grid container spacing={3}>
                                {[
                                    { label: "Code Reviews", val: "Strict" },
                                    { label: "Test Coverage", val: "90%+" },
                                    { label: "CI/CD Pipelines", val: "Automated" },
                                    { label: "Cloud Native", val: "100%" }
                                ].map((stat, i) => (
                                    <Grid item xs={12} sm={6} key={i}>
                                        <Paper sx={{ p: { xs: 3, md: 5 }, bgcolor: 'rgba(255,255,255,0.05)', textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, transition: '0.3s', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                                            <Typography variant="h3" fontWeight={800} color="#69F0AE" mb={1} sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, wordBreak: 'break-word' }}>{stat.val}</Typography>
                                            <Typography variant="subtitle1" color="#fff" fontWeight={600} letterSpacing={1} sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>{stat.label}</Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Container>
            </Box>



            {/* 🔹 LIFE AT GAT - NEW GALLERY SECTION */}
            <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#FAFAFA' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={8} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <SectionHeader tagline="Culture" title="Life at GrowAITech" subtitle="We work hard, play hard, and celebrate every win. Our culture is built on curiosity, inclusivity, and continuous learning." />
                            <Button variant="outlined" sx={{ borderRadius: 50, px: 4, borderColor: '#034522', color: '#034522', fontWeight: 700 }}>
                                Join Our Team
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                                <Box sx={{ gridColumn: 'span 8', borderRadius: 4, overflow: 'hidden', height: 250 }}>
                                    <Box component="img" src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </Box>
                                <Box sx={{ gridColumn: 'span 4', borderRadius: 4, overflow: 'hidden', height: 250 }}>
                                    <Box component="img" src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=800&q=80" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </Box>
                                <Box sx={{ gridColumn: 'span 4', borderRadius: 4, overflow: 'hidden', height: 200 }}>
                                    <Box component="img" src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </Box>
                                <Box sx={{ gridColumn: 'span 8', borderRadius: 4, overflow: 'hidden', height: 200 }}>
                                    <Box component="img" src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=80" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 🔹 THE GAT PHILOSOPHY (New Motivational Section) */}
            <Box sx={{ py: { xs: 8, md: 15 }, position: 'relative', overflow: 'hidden', bgcolor: '#001e0f', color: 'white' }}>
                {/* Abstract Background Shapes */}
                <Box sx={{ position: 'absolute', top: 0, right: 0, width: '600px', height: '100%', background: 'linear-gradient(90deg, transparent, #00331a)', zIndex: 0 }} />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={8} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Typography variant="overline" sx={{ color: '#69F0AE', letterSpacing: 3, fontWeight: 700 }}>Our Philosophy</Typography>
                            <Typography variant="h3" fontWeight={800} sx={{ mt: 2, mb: 4, lineHeight: 1.2 }}>
                                We Don't Just Build Software.<br />
                                <span style={{ color: '#1A9E7F' }}>We Engineer Breakthroughs.</span>
                            </Typography>
                            <Typography paragraph sx={{ fontSize: '1.2rem', lineHeight: 2, opacity: 0.9, mb: 4 }}>
                                In a world where technology is commoditized, <strong>vision is the only differentiator.</strong> At GrowAITech, we believe that true innovation lies at the intersection of bold ambition and flawless execution. We exist to challenge the status quo, to turn "impossible" into "done," and to empower visionaries to reshape their industries.
                            </Typography>
                            <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, opacity: 0.8 }}>
                                Every line of code we write is a promise of performance. Every strategy we devise is a blueprint for dominance. We are not just service providers; we are your growth partners in the most literal sense. When you win, we win.
                            </Typography>
                            <Button variant="outlined" size="large" sx={{ mt: 4, borderRadius: 0, borderColor: '#69F0AE', color: '#69F0AE', px: 5, py: 1.5, '&:hover': { bgcolor: 'rgba(105, 240, 174, 0.1)', borderColor: '#fff', color: '#fff' } }}>
                                Read Our Manifesto
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Paper elevation={10} sx={{ p: 5, bgcolor: '#002613', borderRadius: 0, borderLeft: '6px solid #69F0AE', position: 'relative' }}>
                                <Typography variant="h1" sx={{ position: 'absolute', top: -30, left: 20, fontSize: '8rem', color: 'white', fontWeight: 900, lineHeight: 0 }}>
                                    “
                                </Typography>
                                <Typography variant="h6" fontStyle="italic" sx={{ lineHeight: 1.8, mb: 3, fontWeight: 300, color: 'white' }}>
                                    "The future belongs to those who build it. At GrowAITech, we are not waiting for the future to happen. We are coding it, designing it, and deploying it every single day. Join us, and let's build something legendary."
                                </Typography>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Box component="img" src="https://ui-avatars.com/api/?name=Kavin+Rajendran&background=1A9E7F&color=fff" sx={{ width: 50, height: 50, borderRadius: '50%' }} />
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'white' }}>Kavin Rajendran</Typography>
                                        <Typography variant="caption" color="#69F0AE">Founder & MD, GrowAITech</Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 🔹 GLOBAL REACH & WHY CHOOSE US (New Section) */}
            <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={8} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>Global Scale</Typography>
                            <Typography variant="h3" fontWeight={800} sx={{ color: '#034522', mt: 1, mb: 3 }}>Innovation Knows No Borders</Typography>
                            <Typography paragraph sx={{ lineHeight: 1.8, color: '#222', fontSize: '1.1rem', fontWeight: 500 }}>
                                From the bustling tech hubs of Silicon Valley to the emerging markets of Asia, GrowAITech is powering the next generation of digital enterprises. We believe that talent is universal, and opportunity should be too.
                            </Typography>
                            <Typography paragraph sx={{ lineHeight: 1.8, color: '#222', fontSize: '1.1rem', fontWeight: 500 }}>
                                Our distributed team operates across multiple time zones, ensuring that innovation never sleeps. Whether you are a startup looking to disrupt or an enterprise aiming to scale, we bring global expertise to your doorstep.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={4} sx={{ p: 5, borderRadius: 4, bgcolor: '#002613', color: 'white' }}>
                                <Typography variant="h5" fontWeight={700} mb={3}>Why Partner With Us?</Typography>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    {[
                                        "100+ Successful Digital Transformations",
                                        "Agile Development with Rapid Prototyping",
                                        "Bank-Grade Security & Compliance",
                                        "24/7 Global Support Infrastructure",
                                        "Dedicated Success Managers for Every account"
                                    ].map((item, i) => (
                                        <Box key={i} display="flex" alignItems="center" gap={2}>
                                            <VerifiedUserIcon sx={{ color: '#69F0AE' }} />
                                            <Typography variant="body1" fontWeight={500}>{item}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 🔹 SOCIAL IMPACT */}
            <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#FAFAFA' }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={10}>
                        <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>Beyond Business</Typography>
                        <Typography variant="h3" fontWeight={800} sx={{ color: '#034522', mt: 1 }}>Impact That Matters</Typography>
                        <Typography variant="body1" sx={{ maxWidth: '700px', mx: 'auto', mt: 2, color: '#333', fontSize: '1.1rem', fontWeight: 500 }}>
                            We believe technology is a force for good. GrowAITech is committed to sustainable practices and community empowerment.
                        </Typography>
                    </Box>
                    <Grid container spacing={4}>
                        {[
                            { title: "Green Coding", desc: "Optimizing algorithms to reduce server energy consumption by 40%." },
                            { title: "Tech Education", desc: "Free coding bootcamps for underrepresented communities in Chennai." },
                            { title: "Open Source", desc: "Contributing back to the libraries and frameworks that power the web." }
                        ].map((impact, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Paper elevation={0} sx={{ p: 5, height: '100%', bgcolor: '#fff', border: '1px solid #eee', borderRadius: 4, transition: '0.3s', '&:hover': { transform: 'translateY(-5px)', borderColor: '#1A9E7F', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' } }}>
                                    <Typography variant="h5" fontWeight={700} color="#034522" mb={2}>{impact.title}</Typography>
                                    <Typography variant="body1" color="text.secondary" lineHeight={1.8}>{impact.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 🔹 FINAL CTA (New) */}
            <Box sx={{ py: 10, textAlign: 'center', bgcolor: '#F0F9F6' }}>
                <Container maxWidth="md">
                    <Typography variant="h3" fontWeight={900} color="#034522" mb={2}>Ready to Write History?</Typography>
                    <Typography variant="h6" color="text.secondary" mb={4} fontWeight={300}>
                        The future doesn't wait. Neither should you. Let's build something extraordinary together.
                    </Typography>
                    <Button variant="contained" size="large" onClick={() => navigate('/contact')} sx={{ px: 6, py: 1.5, borderRadius: 50, fontSize: '1.1rem', bgcolor: '#034522', color: '#fff', '&:hover': { bgcolor: '#1A9E7F' } }}>
                        Start Your Project
                    </Button>
                </Container>
            </Box>

            {/* 🔹 TECHNOLOGY LEADERSHIP (Progress Bars) */}
            <Box sx={{ py: 12, bgcolor: '#002613', color: 'white' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={8} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography variant="overline" sx={{ color: '#1A9E7F', letterSpacing: 2, fontWeight: 700 }}>Expertise</Typography>
                            <Typography variant="h3" fontWeight={800} sx={{ mt: 1, mb: 3 }}>Mastering the Tech Stack</Typography>
                            <Typography paragraph sx={{ opacity: 0.8, lineHeight: 1.8, fontSize: '1.1rem' }}>
                                We stay ahead of the technology curve so you don't have to. Our team creates enterprise-grade solutions using the most reliable and advanced tools available.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {expertise.map((item, idx) => (
                                <Box key={idx} mb={4}>
                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                        <Typography fontWeight={600}>{item.area}</Typography>
                                        <Typography fontWeight={600}>{item.level}%</Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={item.level}
                                        sx={{
                                            height: 8,
                                            borderRadius: 4,
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            '& .MuiLinearProgress-bar': { bgcolor: item.color }
                                        }}
                                    />
                                </Box>
                            ))}
                        </Grid>
                    </Grid>
                </Container>
            </Box>

        </Box>
    );
}

export default About;
